/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * SyllabusContext
 * Context API for managing syllabus upload state
 *
 * The context automatically handles:
- File upload to signed URL
- Progress simulation during API calls
- Syllabus analysis API call
- Assignment extraction and storage
- Error handling and user feedback
- Automatic tab switching on success
- Bulk edit mode handling
 */



import React, { createContext, useContext, useReducer, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SYLLABUS_ACTIONS, BULK_EDIT_MODE, DEFAULT_TAB_ORDER, TAB_IDS } from '../common/constants';
import initialState from './InitialStates/SyllabusInitialState';
import syllabusReducer from './Reducers/SyllabusReducer';
import Utils from '../common/utils';
import ValidationUtils from '../common/validationUtils';
import usePrevious from '../hooks/usePervious';


// Create contexts
const SyllabusStateContext = createContext();
const SyllabusDispatchContext = createContext();

// Provider component
export function SyllabusProvider({ children,
  onUploadComplete,
  onFileError,
  onAddNewClick,
  onEditClick,
  onConfirmClick,
  initialSyllabusInfo,
  runCoverageDedupeOnSync,
  currentAssignedList,
  parentApp,
  ctaLabels,
  config,
  tocData,
  restrictAlreadyAssigned,
  assignmentDefaults,
  onAssignmentDefaultsChange,
  tabOrder,
  syllabusDateCallbacks }) {
// Initialize reducer state with optional initial syllabus payload

  const isBulkEditMode = config?.mode === BULK_EDIT_MODE;

  // Helper to get tab index by tab ID (respects custom tabOrder)
  const getTabIndexById = useCallback(
    (tabId) => Utils.getTabIndexById(tabId, tabOrder),
    [tabOrder]
  );

  // Get the initial tab value based on tabOrder
  const getInitialTabValue = () => {
    if (initialSyllabusInfo && !isBulkEditMode) {
      return Utils.getTabIndexById(TAB_IDS.ADD_MANUALLY, tabOrder);
    }

    return initialState.tabValue;
  };

  const init = {
    ...initialState,
    syllabusInfo: initialSyllabusInfo || initialState.syllabusInfo,
    assignments: initialSyllabusInfo?.data?.schedule || initialState.assignments,
    tabValue: getInitialTabValue(),
    currentAssignedList: currentAssignedList || initialState.currentAssignedList,
    assignmentDefaults: assignmentDefaults ?? initialState.assignmentDefaults
  };
  const [state, dispatch] = useReducer(syllabusReducer, init);

  const prevInitialSyllabusInfoRef = useRef(initialSyllabusInfo);
  const lastCoverageDedupeTokenRef = useRef(Number(runCoverageDedupeOnSync) || 0);

  // track previous last date and time to detect changes
  const prevLastDefaults = usePrevious(assignmentDefaults);

  // Parent store push → child re-sync. Dedupe once per TOC token bump (see getSyllabusProps).
  useEffect(() => {
    const prevValue = prevInitialSyllabusInfoRef.current;
    const currentValue = initialSyllabusInfo;

    if (prevValue !== currentValue) {
      const syncToken = Number(runCoverageDedupeOnSync) || 0;
      const shouldDedupe = syncToken > lastCoverageDedupeTokenRef.current;
      if (shouldDedupe) {
        lastCoverageDedupeTokenRef.current = syncToken;
      }

      dispatch({
        type: SYLLABUS_ACTIONS.SET_INITIAL_SYLLABUS,
        payload: currentValue,
        tocData,
        restrictAlreadyAssigned,
        runCoverageDedupe: shouldDedupe,
        targetTabValue: Utils.getTabIndexById(TAB_IDS.ADD_MANUALLY, tabOrder)
      });
    }
    prevInitialSyllabusInfoRef.current = currentValue;
  }, [initialSyllabusInfo, runCoverageDedupeOnSync, tocData, restrictAlreadyAssigned, tabOrder]);

  useEffect(() => {
    const prev = prevLastDefaults;
    const next = assignmentDefaults;

    const changed =
      (prev?.date ?? null) !== (next?.date ?? null) ||
      (prev?.time ?? null) !== (next?.time ?? null);

    if (changed) {
      dispatch({ type: SYLLABUS_ACTIONS.SET_LAST_ASSIGNMENT_DEFAULTS, payload: next ?? null });
    }
  }, [assignmentDefaults, prevLastDefaults]);


  // Action creators
  const setFile = useCallback((file) => {
    dispatch({ type: SYLLABUS_ACTIONS.SET_FILE, payload: file });
  }, []);

  const removeFile = useCallback(() => {
    dispatch({ type: SYLLABUS_ACTIONS.REMOVE_FILE });
  }, []);

  const setTab = useCallback((tabIndex) => {
    dispatch({ type: SYLLABUS_ACTIONS.SET_TAB, payload: tabIndex });
  }, []);

  const addAssignment = useCallback((newAssignment) => {
    dispatch({ type: SYLLABUS_ACTIONS.ADD_ASSIGNMENT, payload: newAssignment });
    const defaults = { date: newAssignment?.date || '', time: newAssignment?.time || '' };
    dispatch({ type: SYLLABUS_ACTIONS.SET_LAST_ASSIGNMENT_DEFAULTS, payload: defaults });
    onAssignmentDefaultsChange?.(defaults);
    onAddNewClick(newAssignment);
  }, [onAddNewClick, onAssignmentDefaultsChange]);

  const updateAssignment = useCallback((index, updatedAssignment) => {
    dispatch({
      type: SYLLABUS_ACTIONS.UPDATE_ASSIGNMENT,
      payload: { index, assignment: updatedAssignment }
    });
    const updatedList = [...state.assignments];
    updatedList[index] = updatedAssignment;
    onEditClick({ assignment: updatedAssignment, allAssignments: updatedList });
  }, [onEditClick, state.assignments]);

  const deleteAssignment = useCallback((index) => {
    dispatch({
      type: SYLLABUS_ACTIONS.DELETE_ASSIGNMENT,
      payload: { index }
    });
  }, []);

  const setDateAdjustParams = useCallback((params) => {
    dispatch({
      type: SYLLABUS_ACTIONS.SET_DATE_ADJUST_PARAMS,
      payload: params
    });
  }, []);

  // Only for bulk updates without onEditClick calls
  const bulkUpdateAssignments = useCallback((assignments) => {
    dispatch({
      type: SYLLABUS_ACTIONS.BULK_UPDATE_ASSIGNMENTS,
      payload: assignments
    });
  }, []);

  // Handle external upload completion (e.g., from channels component)
  const handleExternalUploadComplete = useCallback((result) => {
    // Context decides the tab value based on parentApp and tabOrder
    const targetTabValue = parentApp === 'aet'
      ? Utils.getTabIndexById(TAB_IDS.ADD_MANUALLY, tabOrder)
      : Utils.getTabIndexById(TAB_IDS.UPLOAD, tabOrder);

    const {
      validatedAssignments,
      validationResult,
      dateNormalizationSummary
    } = ValidationUtils.processScheduleAfterUpload({
      schedule: result.syllabusInfo?.data?.schedule || [],
      tocData,
      chapterMcqMapping: result.chapterMcqMapping || null,
      restrictAlreadyAssigned,
      syllabusDateCallbacks
    });

    const validatedResult = {
      ...result,
      syllabusInfo: {
        ...result.syllabusInfo,
        assignments: validatedAssignments,
        dateNormalizationSummary,
        data: {
          ...result.syllabusInfo?.data,
          schedule: validatedAssignments
        },
        validation: {
          isValid: validationResult.isValid,
          totalCount: validatedAssignments.length,
          validCount: validatedAssignments.length - validationResult.invalidCount,
          invalidCount: validationResult.invalidCount,
          invalidAssignments: validationResult.invalidAssignments
        }
      },
      assignments: validatedAssignments
    };

    // Call parent callback with validated result
    if (onUploadComplete) {
      setTimeout(() => {
        onUploadComplete(validatedResult);
      }, 100);
    }

    dispatch({
      type: SYLLABUS_ACTIONS.UPLOAD_SUCCESS,
      payload: {
        ...validatedResult,
        tabValue: isBulkEditMode ? Utils.getTabIndexById(TAB_IDS.UPLOAD, tabOrder) : targetTabValue,
        isBulkEditMode,
        tocData,
        restrictAlreadyAssigned
      }
    });
  }, [
    parentApp,
    onUploadComplete,
    isBulkEditMode,
    tocData,
    restrictAlreadyAssigned,
    getTabIndexById,
    tabOrder,
    syllabusDateCallbacks
  ]);

  const handleUploadError = useCallback((error) => {
    dispatch({
      type: SYLLABUS_ACTIONS.UPLOAD_ERROR,
      payload: error
    });
    console.warn('handleUploadError called from context', error);
    if (onFileError) {
      onFileError(error);
    }
  }, [onFileError]);

  // Actions object - memoized to prevent unnecessary re-renders
  const actions = useMemo(() => ({
    setFile,
    removeFile,
    setTab,
    addAssignment,
    updateAssignment,
    deleteAssignment,
    setDateAdjustParams,
    bulkUpdateAssignments,
    handleExternalUploadComplete,
    handleUploadError,
    onConfirmClick, // no dispatch for now
    getTabIndexById // expose for components that need to set tabs by ID
  }), [
    setFile,
    removeFile,
    setTab,
    addAssignment,
    updateAssignment,
    deleteAssignment,
    setDateAdjustParams,
    bulkUpdateAssignments,
    handleExternalUploadComplete,
    handleUploadError,
    onConfirmClick,
    getTabIndexById
  ]);

  // Memoize the value for SyllabusStateContext.Provider to prevent unnecessary re-renders
  const memoizedStateValue = useMemo(() => ({ ...state, ctaLabels }), [state, ctaLabels]);

  return (
    <SyllabusStateContext.Provider value={memoizedStateValue}>
      <SyllabusDispatchContext.Provider value={actions}>
        {children}
      </SyllabusDispatchContext.Provider>
    </SyllabusStateContext.Provider>
  );
}

SyllabusProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialSyllabusInfo: PropTypes.object,
  /** TOC mapping sync token from parent (0 = no dedupe). Bumped only on Prism/Berlin complete. */
  runCoverageDedupeOnSync: PropTypes.number,
  onUploadComplete: PropTypes.func,
  onFileError: PropTypes.func,
  onAddNewClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onConfirmClick: PropTypes.func,
  parentApp: PropTypes.string,
  ctaLabels: PropTypes.shape({
    upload: PropTypes.string,
    confirm: PropTypes.string
  }),
  config: PropTypes.shape({
    mode: PropTypes.string
  }),
  currentAssignedList: PropTypes.array,
  tocData: PropTypes.array,
  restrictAlreadyAssigned: PropTypes.bool,
  assignmentDefaults: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string
  }),
  onAssignmentDefaultsChange: PropTypes.func,
  tabOrder: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number.isRequired,
      id: PropTypes.string.isRequired
    })
  ),
  syllabusDateCallbacks: PropTypes.shape({
    normalizeResponseDates: PropTypes.func
  })
};

SyllabusProvider.defaultProps = {
  initialSyllabusInfo: null,
  runCoverageDedupeOnSync: 0,
  onUploadComplete: () => {},
  onFileError: () => {},
  onAddNewClick: () => {},
  onEditClick: () => {},
  onConfirmClick: () => {},
  parentApp: 'aet',
  ctaLabels: {
    upload: 'Upload',
    confirm: 'Confirm'
  },
  config: {
  },
  currentAssignedList: null,
  tocData: null,
  restrictAlreadyAssigned: true,
  assignmentDefaults: null,
  onAssignmentDefaultsChange: () => {},
  tabOrder: DEFAULT_TAB_ORDER,
  syllabusDateCallbacks: {}
};

// Custom hooks to use the context
export function useSyllabusState() {
  const context = useContext(SyllabusStateContext);
  if (context === undefined) {
    throw new Error('useSyllabusState must be used within a SyllabusProvider');
  }

  return context;
}

export function useSyllabusActions() {
  const context = useContext(SyllabusDispatchContext);
  if (context === undefined) {
    throw new Error('useSyllabusActions must be used within a SyllabusProvider');
  }

  return context;
}

// Combined hook for convenience
export function useSyllabus() {
  return {
    state: useSyllabusState(),
    actions: useSyllabusActions()
  };
}

export default SyllabusProvider;
